// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.
import * as Adaptive from 'adaptivecards';
import hostConfig from './HostConfigs/OutlookDesktop.json';
import CustomTextInput from '../components/ActionableMessages/CustomTextInput';
import CustomNumberInput from '../components/ActionableMessages/CustomNumberInput';
import CustomChoiceSetInput from '../components/ActionableMessages/CustomChoiceSetInput';
import CustomDateInput from '../components/ActionableMessages/CustomDateInput';
import { initialize as initializeCustomContainer } from '../components/ActionableMessages/CustomContainer';
import { initialize as initializeAdaptiveCard } from '../components/ActionableMessages/CustomAdaptiveCard';
import { initialize as initializeCustomColumnSet } from '../components/ActionableMessages/CustomColumnSet';
import CustomToggleInput from '../components/ActionableMessages/CustomToggleInput';
import { initialize as initializeCustomAction } from '../components/ActionableMessages/CustomAction';
import { initialize as initializeCustomImageSet } from '../components/ActionableMessages/CustomImageSet';
import CustomTextBlock from '../components/ActionableMessages/CustomTextBlock';

const parsePadding = (json: any): Adaptive.PaddingDefinition => {
    if (json) {
        if (typeof json === 'string') {
            let uniformPadding = Adaptive.getEnumValue(
                Adaptive.Spacing,
                json,
                Adaptive.Spacing.None
            );

            return new Adaptive.PaddingDefinition(
                uniformPadding,
                uniformPadding,
                uniformPadding,
                uniformPadding
            );
        } else if (typeof json === 'object') {
            return new Adaptive.PaddingDefinition(
                Adaptive.getEnumValue(
                    Adaptive.Spacing,
                    json['top'],
                    Adaptive.Spacing.None
                ),
                Adaptive.getEnumValue(
                    Adaptive.Spacing,
                    json['right'],
                    Adaptive.Spacing.None
                ),
                Adaptive.getEnumValue(
                    Adaptive.Spacing,
                    json['bottom'],
                    Adaptive.Spacing.None
                ),
                Adaptive.getEnumValue(
                    Adaptive.Spacing,
                    json['left'],
                    Adaptive.Spacing.None
                )
            );
        }
    }

    return null;
};

export const parseElement = (element: Adaptive.CardElement, json: any) => {
    if (element instanceof Adaptive.AdaptiveCard) {
        const card = element as Adaptive.AdaptiveCard;
        const actionArray: Array<Adaptive.Action> = [];

        card['resources'] = { actions: actionArray };

        if (typeof json['resources'] === 'object') {
            const actionResources = json['resources']['actions'] as Array<any>;

            for (let i = 0; i < actionResources.length; i++) {
                let action = Adaptive.AdaptiveCard.actionTypeRegistry.createInstance(
                    actionResources[i]['type']
                );

                if (action) {
                    action.parse(actionResources[i]);
                    action.setParent(card);

                    actionArray.push(action);
                }
            }
        }
    }

    if (typeof json['isVisible'] === 'boolean') {
        element.isVisible = json['isVisible'];
    }

    if (
        element instanceof Adaptive.Image &&
        typeof json['backgroundColor'] === 'string'
    ) {
        element.backgroundColor = json['backgroundColor'];
    }

    if (element instanceof Adaptive.Container) {
        const padding = parsePadding(json['padding']);

        if (padding) {
            (element as Adaptive.Container).padding = padding;
        }
    }

    if (element instanceof Adaptive.ColumnSet) {
        const padding = parsePadding(json['padding']);

        if (padding) {
            (element as Adaptive.ColumnSet).padding = padding;
        }
    }
};

export const anchorClicked = (
    element: Adaptive.CardElement,
    anchor: HTMLAnchorElement
): boolean => {
    let regEx = /^action:([a-z0-9]+)$/gi;
    let rootCard = element.getRootElement() as Adaptive.AdaptiveCard;
    let matches = regEx.exec(anchor.href);

    if (matches) {
        let actionId = matches[1];

        if (rootCard) {
            let actionArray = rootCard['resources']['actions'] as Array<
                Adaptive.Action
            >;

            for (let i = 0; i < actionArray.length; i++) {
                if (actionArray[i].id === actionId) {
                    actionArray[i].execute();

                    return true;
                }
            }
        }
    }

    return false;
};

export const actionExecuted = (action: Adaptive.Action) => {
    let message: string = 'Action executed\n';
    message += '    Title: ' + action.title + '\n';

    if (action instanceof Adaptive.OpenUrlAction) {
        message += '    Type: OpenUrl\n';
        message += '    Url: ' + (action as Adaptive.OpenUrlAction).url + '\n';
    } else if (action instanceof Adaptive.SubmitAction) {
        message += '    Type: Submit';
        message +=
            '    Data: ' +
            JSON.stringify((action as Adaptive.SubmitAction).data);
    } else if (action instanceof Adaptive.HttpAction) {
        const httpAction = action as Adaptive.HttpAction;
        message += '    Type: Http\n';
        message += '    Url: ' + httpAction.url + '\n';
        message += '    Method: ' + httpAction.method + '\n';
        message += '    Headers:\n';

        for (let i = 0; i < httpAction.headers.length; i++) {
            message +=
                '        ' +
                httpAction.headers[i].name +
                ': ' +
                httpAction.headers[i].value +
                '\n';
        }

        message += '    Body: ' + httpAction.body + '\n';
    } else {
        message += '    Type: <unknown>';
    }

    alert(message);
};

export default class HostContainer {
    private _cardHost: HTMLElement;

    constructor() {
        this._cardHost = document.createElement('div');
        this._cardHost.className = 'cardHost';
    }

    public renderTo(hostElement: HTMLElement) {
        hostElement.classList.add('outlook-frame');
        hostElement.appendChild(this.cardHost);
    }

    public initialize() {
        initializeAdaptiveCard();
        initializeCustomContainer();
        initializeCustomColumnSet();
        initializeCustomAction();
        initializeCustomImageSet();
        Adaptive.AdaptiveCard.elementTypeRegistry.reset();
        Adaptive.AdaptiveCard.actionTypeRegistry.reset();

        Adaptive.AdaptiveCard.elementTypeRegistry.unregisterType('Input.Text');
        Adaptive.AdaptiveCard.elementTypeRegistry.registerType(
            'Input.Text',
            () => {
                return new CustomTextInput();
            }
        );

        Adaptive.AdaptiveCard.elementTypeRegistry.unregisterType(
            'Input.Toggle'
        );
        Adaptive.AdaptiveCard.elementTypeRegistry.registerType(
            'Input.Toggle',
            () => {
                return new CustomToggleInput();
            }
        );

        Adaptive.AdaptiveCard.elementTypeRegistry.unregisterType(
            'Input.Number'
        );
        Adaptive.AdaptiveCard.elementTypeRegistry.registerType(
            'Input.Number',
            () => {
                return new CustomNumberInput();
            }
        );

        Adaptive.AdaptiveCard.elementTypeRegistry.registerType('Column', () => {
            return new Adaptive.Column();
        });

        Adaptive.AdaptiveCard.elementTypeRegistry.unregisterType(
            'Input.ChoiceSet'
        );
        Adaptive.AdaptiveCard.elementTypeRegistry.registerType(
            'Input.ChoiceSet',
            () => {
                return new CustomChoiceSetInput();
            }
        );

        Adaptive.AdaptiveCard.elementTypeRegistry.unregisterType('Input.Date');
        Adaptive.AdaptiveCard.elementTypeRegistry.registerType(
            'Input.Date',
            () => {
                return new CustomDateInput();
            }
        );

        Adaptive.AdaptiveCard.elementTypeRegistry.unregisterType('TextBlock');
        Adaptive.AdaptiveCard.elementTypeRegistry.registerType(
            'TextBlock',
            () => {
                return new CustomTextBlock();
            }
        );

        Adaptive.AdaptiveCard.actionTypeRegistry.unregisterType(
            'Action.Submit'
        );
        Adaptive.AdaptiveCard.actionTypeRegistry.registerType(
            'Action.Http',
            () => {
                return new Adaptive.HttpAction();
            }
        );
    }

    public getBackgroundColor(): string {
        return '#FFF';
    }

    supportsActionBar: boolean = false;

    get cardHost(): HTMLElement {
        return this._cardHost;
    }

    get isFixedHeight(): boolean {
        return false;
    }

    public getHostConfig(): Adaptive.HostConfig {
        return new Adaptive.HostConfig(hostConfig);
    }
}
